import { Delete } from "@mui/icons-material";
import { Button, IconButton, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { getCategory, updateCategory } from "api/services/categories";
import DrawerWrapper from "components/DrawerWrapper";
import Loader from "components/Loader";
import LoadingButton from "components/LoadingButton";
import SafeTextField from "components/SafeTextField";
import { snack } from "components/toast";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { DialogProps, ResType } from "types";
import { handleError } from "utils/handleError";

interface EditCategoryProps extends DialogProps {
  data: any;
}

function EditCategory({ open, setOpen, data }: EditCategoryProps) {
  const queryClient = useQueryClient();
  const [subCategory, setSubCategory] = useState<string>("");
  const [state, setState] = useState<any>({
    name: "",
    color: "",
    subCategories: [],
  });

  const { isLoading: categoryLoading }: ResType = useQuery(["categories", data.id],
    getCategory, {
    onSuccess: (response) => {
      if (response && response?.data) {
        setState({
          name: response?.data?.name,
          color: response?.data?.color,
          subCategories: response?.data?.subCategories,
        });
      } else {
        setState({
          name: "",
          color: "",
          subCategories: [],
        });
      }
    },
    onError: (err) => {
      if (!err?.error) {
        snack.error("An error occurred while fetching categories");
      }
    },
    enabled: open
  });

  const handleChange = (key: string, v: string) => {
    setState({ ...state, [key]: v });
  };

  const { mutate, isLoading } = useMutation(updateCategory, {
    onSuccess: () => {
      snack.success("Service Category Updated");
      setOpen(false);
      queryClient.invalidateQueries("categories");
    },
    onError: (err: any) => {
      if (err?.response?.response?.data?.message?.[0].includes('subCategories')) {
        snack.error('Subcategory name cannot contain /, |, *, ?, ", <, >');
      } else {
        snack.error(handleError(err));
        setOpen(false);
        setState({
          name: "",
          color: "",
          subCategories: [],
        })
      }

    },
  });

  const addSubCategory = () => {
    if (!subCategory) return snack.error("Enter Subcategory");
    if (/[\/\|\*\?"<>]/.test(subCategory)) return snack.error('Subcategory name cannot contain /, |, *, ?, ", <, >')

    setState({
      ...state,
      subCategories: [...state.subCategories, { name: subCategory }],
    });
    setSubCategory("");
  };

  const deleteSubCategory = (index: any) => {
    let subCategories = [...state.subCategories];
    subCategories.splice(index, 1);
    setState({
      ...state,
      subCategories: subCategories,
    });
  };

  const handleSubmit = () => {
    if (!state.name) return snack.error("Name is required");
    mutate({
      id: data.id,
      data: state,
    });
  };

  return (categoryLoading ? <Loader /> :
    <DrawerWrapper open={open} setOpen={setOpen} title="Edit Category">
      <SafeTextField
        InputLabelProps={{ shrink: true }}
        variant="outlined"
        fullWidth
        value={state.name}
        disabled
        onChange={(e) => handleChange("name", e.target.value)}
        size="small"
        label="Name"
      />
      <Box display="flex" gap={1} mt={3}>
        <TextField
          variant="outlined"
          fullWidth
          value={subCategory}
          onChange={(e) => setSubCategory(e.target.value)}
          size="small"
          label="Add Subcategory"
        />
        <Button
          onClick={addSubCategory}
          sx={{ minWidth: 80 }}
          variant="outlined"
          color="primary"
        >
          + Add
        </Button>
      </Box>
      <Box my={2}>
        {state.subCategories.map((item, index) => (
          <Box
            key={index}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
            gap={1}
          >
            <Typography variant="subtitle2">
              {index + 1}. {item.name}
            </Typography>
            {!state.defaultOne && <IconButton size="small" onClick={() => deleteSubCategory(index)}>
              <Delete />
            </IconButton>}
          </Box>
        ))}
      </Box>
      <TextField
        sx={{ mt: 2, minWidth: 200 }}
        InputProps={{ sx: { padding: "0px" } }}
        variant="outlined"
        size="small"
        value={state.color}
        onChange={(e) => handleChange("color", e.target.value)}
        label="Choose Color"
        name="color"
        type="color"
        required
      />
      <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
        <LoadingButton
          onClick={handleSubmit}
          loading={isLoading}
          disabled={isLoading}
          fullWidth
          loadingColor="white"
          title="Update"
          color="secondary"
        />
      </Box>
    </DrawerWrapper>
  );
}

export default EditCategory;
